import { z } from "zod";
import { collapseWhitespace, looksMisencoded } from "../text.js";
/** A replacement character means the bytes were already wrong on arrival. */
const MISENCODED = "This text arrived damaged; check the encoding of what sent it.";
/**
 * Task 9.13 — correcting a word the cellar decided once.
 *
 * Six things here are per-cellar reference rows created by being typed, and
 * until now not one of them could be corrected: a producer entered as `Vivnio`
 * was suggested by the picker forever. These are the six.
 *
 * The kinds are named as they are in the interface rather than after their
 * tables, because this list is read by a screen and each one needs a heading.
 */
export const vocabularyKindSchema = z.enum([
    "producers",
    "regions",
    "grapes",
    "styles",
    "ratingSources",
    "purchaseSources",
]);
/**
 * One word, and how much of the cellar rests on it.
 *
 * `uses` is what makes the question answerable before it is asked — *three
 * lots move from Vivnio to Vivino* is this number, and a row with none is a
 * word nothing would notice losing.
 */
export const vocabularyRowSchema = z.object({
    id: z.string(),
    name: z.string(),
    /**
     * The sort key the server matched on, sent rather than recomputed so the
     * screen cannot disagree with the index about what counts as the same word.
     */
    sortKey: z.string(),
    uses: z.number().int().nonnegative(),
    /**
     * A region's country, and null for the other five.
     *
     * Regions are unique on `(cellar, country, sortKey)` rather than on two
     * columns, because Rioja in Spain and a Rioja somewhere else are not the
     * same place. So a rename merges only within one country, and the screen
     * needs the country to know which row it would merge into.
     *
     * Nullable it stays and the default came off on 2026-09-22: *this row has
     * no country* is five of the six kinds, and *the API did not say* is drift.
     */
    countryCode: z.string().nullable(),
    /**
     * Whether this row may be merged into another, or another into it.
     *
     * False for exactly one row: the cellar's own rating source. It means *what
     * I think of this wine*, which `project-scope.md` is explicit is not
     * comparable with what Vivino or Munskänkarna think, and a merge would
     * conflate the two beyond any later separating. It may still be renamed.
     *
     * **The default was `true` and it was the second dangerous one**, found in
     * the same walk as `9.2`'s `seesPrices` and wrong in the same way: a
     * default that is right for almost every row is a default that hides an API
     * which has stopped answering. An absent field would have made the cellar's
     * own rating source mergeable — the one row this exists to protect — and
     * the screen would have looked entirely normal offering it. Required as of
     * 2026-09-22.
     */
    mergeable: z.boolean(),
});
export const vocabularyResponseSchema = z.object({
    producers: z.array(vocabularyRowSchema),
    regions: z.array(vocabularyRowSchema),
    grapes: z.array(vocabularyRowSchema),
    styles: z.array(vocabularyRowSchema),
    ratingSources: z.array(vocabularyRowSchema),
    purchaseSources: z.array(vocabularyRowSchema),
});
/**
 * Calling a row by a name — and that is the whole of the operation.
 *
 * There is no `merge` flag, because there is no choice to offer. Every row is
 * unique on its cellar and sort key, so a name another row already holds
 * cannot produce two rows; the index refuses it. A free sort key renames and a
 * taken one merges, and which of the two happens is decided by the data rather
 * than by a button. See the decision in `project-scope.md`, 2026-09-17.
 */
export const renameVocabularyRequestSchema = z.object({
    name: z
        .string()
        .transform(collapseWhitespace)
        .pipe(z.string().min(1).max(120))
        .refine((v) => !looksMisencoded(v), { message: MISENCODED }),
});
/**
 * What actually happened, said in the words of what was done to the cellar.
 *
 * `moved` is only meaningful for a merge and is the number of wines, lots or
 * ratings that changed hands. A rename moves nothing — the row keeps every
 * reference it had, which is the point of renaming rather than retyping.
 *
 * `dropped` counts references a merge had to discard rather than move: a wine
 * already carrying both grapes, or one already rated by both sources on the
 * same day. Those are duplicates by the very claim the merge makes, and they
 * are counted so the screen can say so rather than quietly losing them.
 */
export const renameVocabularyResponseSchema = z.object({
    outcome: z.enum(["renamed", "merged"]),
    name: z.string(),
    moved: z.number().int().nonnegative(),
    dropped: z.number().int().nonnegative(),
});
//# sourceMappingURL=vocabulary.js.map